import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export default function Navigation() {
  const { user, login, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  const isActive = (path) => location.pathname === path;

  const verbCls = (active) =>
    `lowercase text-xs sm:text-sm lg:text-base transition-all duration-300 whitespace-nowrap px-3 py-1.5 rounded-full ${
      active
        ? 'text-white border border-[#4a514a]'
        : 'text-white/40 hover:text-white/80 border border-transparent'
    }`;

  return (
    <nav
      className="w-full bg-[#141a15]/80 backdrop-blur-xl border-b border-[#1e251f] sticky top-0 z-50"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
      data-testid="navigation"
      role="navigation"
      aria-label="main navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20 gap-3">
          {/* φ — opens the overflow menu */}
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                aria-label="open menu"
                data-testid="nav-logo"
                className="shrink-0 leading-none focus:outline-none -ml-1 p-1 rounded-md cursor-pointer select-none hover:opacity-70 focus-visible:opacity-100 transition-opacity duration-300"
              >
                <img
                  src="/phi-logo-128.png"
                  alt="logOS"
                  className="h-8 sm:h-10 lg:h-12 w-auto"
                  draggable={false}
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              sideOffset={12}
              onCloseAutoFocus={(e) => e.preventDefault()}
              className="bg-[#151b17]/95 backdrop-blur-xl border border-[#232a24] rounded-xl shadow-2xl shadow-black/80 min-w-[14rem] p-1.5"
              data-testid="nav-menu"
            >
              <NavMenuLink to="/" testid="menu-home" onSelect={closeMenu}>home</NavMenuLink>
              <NavMenuLink to="/about" testid="menu-about" onSelect={closeMenu}>about</NavMenuLink>
              <NavMenuLink to="/downloads" testid="menu-downloads" onSelect={closeMenu}>downloads</NavMenuLink>
              <NavMenuLink to="/developments" testid="menu-developments" onSelect={closeMenu}>developments</NavMenuLink>
              <NavMenuLink to="/specs" testid="menu-specs" onSelect={closeMenu}>tech specs</NavMenuLink>
              <NavMenuLink to="/paper" testid="menu-paper" onSelect={closeMenu}>paper</NavMenuLink>
              <NavMenuLink to="/info" testid="menu-info" onSelect={closeMenu}>info</NavMenuLink>

              <DropdownMenuSeparator className="bg-[#1e251f] my-1.5 mx-1" />

              {user ? (
                <>
                  <NavMenuLink to="/workspace" testid="menu-workspace" onSelect={closeMenu}>workspace</NavMenuLink>
                  <NavMenuLink to="/data" testid="menu-data" onSelect={closeMenu}>data</NavMenuLink>
                  <NavMenuLink to={`/u/${user.username}`} testid="menu-profile" onSelect={closeMenu}>public profile</NavMenuLink>
                  <NavMenuLink to="/train" testid="menu-train" onSelect={closeMenu}>train</NavMenuLink>
                  <NavMenuLink to="/link" testid="menu-link" onSelect={closeMenu}>linked apps</NavMenuLink>
                  {user.role === 'admin' && (
                    <NavMenuLink to="/admin" testid="menu-admin" onSelect={closeMenu}>admin</NavMenuLink>
                  )}
                  <DropdownMenuItem
                    onSelect={() => { closeMenu(); logout(); }}
                    className="lowercase text-sm text-white/70 hover:!bg-white/5 hover:!text-white focus:!bg-white/5 focus:!text-white px-3 py-2 rounded-md cursor-pointer outline-none"
                    data-testid="menu-logout"
                  >
                    logout
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem
                  onSelect={() => { closeMenu(); login(); }}
                  className="lowercase text-sm text-white/70 hover:!bg-white/5 hover:!text-white focus:!bg-white/5 focus:!text-white px-3 py-2 rounded-md cursor-pointer outline-none"
                  data-testid="menu-login"
                >
                  login with google
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Verbs — right side */}
          <div className="flex items-center gap-2 sm:gap-3 lg:gap-5 flex-wrap justify-end">
            {/* Desktop bonus surface */}
            <Link
              to="/downloads"
              className="hidden lg:inline-flex border border-[#383f38] px-4 lg:px-5 py-1.5 text-sm lg:text-base text-white/70 lowercase hover:bg-white hover:text-black transition-all duration-300 rounded-full glow-hover"
              data-testid="download-soma-btn"
            >
              download soma
            </Link>
            {/* signed in, about lives in the Φ menu so the verbs fit on a phone */}
            {!user && (
              <Link to="/about" className={`inline-flex ${verbCls(isActive('/about'))}`} data-testid="nav-about">
                about
              </Link>
            )}

            {/* Always-visible verbs */}
            <Link to="/checkpoints" className={verbCls(isActive('/checkpoints'))} data-testid="nav-search">
              search
            </Link>
            <Link to="/chat" className={verbCls(isActive('/chat'))} data-testid="nav-chat">
              chat
            </Link>
            {user && (
              <Link to="/upload" className={verbCls(isActive('/upload'))} data-testid="nav-upload">
                upload
              </Link>
            )}
            {user && (
              <Link to="/train" className={verbCls(isActive('/train') || location.pathname.startsWith('/runs/') || location.pathname.startsWith('/remote/'))} data-testid="nav-train">
                train
              </Link>
            )}

            {/* Identity */}
            {user ? (
              <button
                onClick={() => navigate('/workspace')}
                className={`hidden sm:inline-block text-white lowercase font-bold whitespace-nowrap text-xs sm:text-sm lg:text-base transition-all duration-300 px-3 py-1.5 rounded-full max-w-[10ch] sm:max-w-[14ch] truncate ${
                  location.pathname === '/workspace'
                    ? 'border border-[#4a514a]'
                    : 'border border-transparent hover:bg-white/5'
                }`}
                aria-label="your workspace"
                data-testid="nav-user-profile"
                title={user.username}
              >
                {user.username}
              </button>
            ) : (
              <button
                onClick={login}
                className="text-white/40 hover:text-white/80 lowercase text-xs sm:text-sm lg:text-base transition-all duration-300 px-3 py-1.5 rounded-full border border-transparent hover:border-[#383f38]"
                data-testid="nav-login-btn"
              >
                login
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

/* ---------------- internal ---------------- */

function NavMenuLink({ to, children, testid, onSelect }) {
  return (
    <DropdownMenuItem asChild onSelect={onSelect}>
      <Link
        to={to}
        className="lowercase text-sm text-white/70 hover:!bg-white/5 hover:!text-white focus:!bg-white/5 focus:!text-white px-3 py-2 rounded-md cursor-pointer outline-none block"
        data-testid={testid}
      >
        {children}
      </Link>
    </DropdownMenuItem>
  );
}
